import { gql } from '@/shared/graphql/generated/lp';

export const getAllLoanFeesQuery = gql(/* GraphQL */ `
  query GetAllLpLoanFees($loanIds: [BigInt!]!) {
    allLoanFees(filter: { loanId: { in: $loanIds } }) {
      edges {
        node {
          loanId
          totalAmountValueUsd
          totalAmount
        }
      }
    }
  }
`);

export const getLoanCreationTimestampsQuery = gql(/* GraphQL */ `
  query GetLoanCreationTimestamps($loanIds: [BigInt!]!) {
    allLoans(filter: { id: { in: $loanIds } }) {
      nodes {
        id
        createdAtTimestamp
      }
    }
  }
`);

export const getLendingPoolsApyQuery = gql(/* GraphQL */ `
  query GetLendingPoolsApy($asset: ChainflipAsset) {
    lendingPools: allLendingPools(filter: { asset: { equalTo: $asset } }) {
      nodes {
        stats: lendingPoolStatsByPoolId(orderBy: BLOCK_DESC, first: 1) {
          nodes {
            asset
            projectedApy6H
            projectedApy24H
            projectedApy72H
            projectedApy168H
            projectedApy720H
            projectedBorrowApy6H
            projectedBorrowApy24H
            projectedBorrowApy72H
            projectedBorrowApy168H
            projectedBorrowApy720H
          }
        }
      }
    }
  }
`);

export const getLendingLiquiditySnapshotsQuery = gql(/* GraphQL */ `
  query GetLendingLiquiditySnapshots(
    $idSs58: String!
    $minTimestamp: Datetime!
    $maxTimestamp: Datetime!
    $groupBy: [LiquiditySnapshotGroupBy!]!
  ) {
    account: accountByIdSs58(idSs58: $idSs58) {
      snapshots: liquiditySnapshotsByLiquidityProviderId(
        filter: {
          and: [
            { timestamp: { greaterThanOrEqualTo: $minTimestamp } }
            { timestamp: { lessThanOrEqualTo: $maxTimestamp } }
          ]
        }
        orderBy: TIMESTAMP_DESC
      ) {
        agg: groupedAggregates(groupBy: $groupBy) {
          keys
          max {
            lendingLiquidityUsd
          }
        }
      }
    }
  }
`);

export const getAllTimeLendingStats = gql(/* GraphQL */ `
  query GetAllTimeLendingStats {
    loans: allLoanUpdates(condition: { type: BORROWING }) {
      agg: aggregates {
        sum {
          amountValueUsd
        }
      }
    }
    loanFees: allLoanFees {
      agg: aggregates {
        sum {
          poolFeePortionAmountValueUsd
          totalAmountValueUsd
          networkFeePortionAmountValueUsd
        }
      }
    }
  }
`);

export const getLendingAnalyticsChartQuery = gql(/* GraphQL */ `
  query GetLendingAnalyticsChart(
    $assets: [ChainflipAsset!]
    $startDate: Datetime
    $endDate: Datetime
    $feeGroupBy: [LoanFeeGroupBy!]!
    $statsGroupBy: [LendingPoolStatGroupBy!]!
  ) {
    fees: allLoanFees(
      filter: {
        asset: { in: $assets }
        timestamp: { greaterThanOrEqualTo: $startDate, lessThanOrEqualTo: $endDate }
      }
    ) {
      agg: groupedAggregates(groupBy: $feeGroupBy) {
        sum {
          totalAmountValueUsd
        }
        keys
      }
    }
    stats: allLendingPoolStats(
      filter: {
        asset: { in: $assets }
        timestamp: { greaterThanOrEqualTo: $startDate, lessThanOrEqualTo: $endDate }
      }
    ) {
      agg: groupedAggregates(groupBy: $statsGroupBy) {
        average {
          avgUnavailableAmountUsd
          avgTvlValueUsd
        }
        keys
      }
    }
  }
`);

export const getAllLoansQuery = gql(/* GraphQL */ `
  query GetAllLoansQuery(
    $first: Int
    $offset: Int
    $loanId: BigInt
    $idSs58: String
    $filter: LoanFilter!
  ) {
    loans: allLoans(
      first: $first
      offset: $offset
      orderBy: LAST_UPDATED_AT_TIMESTAMP_DESC
      filter: {
        id: { equalTo: $loanId }
        accountByBorrowerId: { idSs58: { equalTo: $idSs58 } }
        and: [$filter]
      }
    ) {
      totalCount
      nodes {
        ...LoanInfo
      }
    }
  }
`);

export const getAllLoanUpdatesQuery = gql(/* GraphQL */ `
  query GetAllLoanUpdates($first: Int, $offset: Int, $loanId: BigInt, $idSs58: String) {
    allLoanUpdates(
      first: $first
      offset: $offset
      orderBy: TIMESTAMP_DESC
      filter: {
        loanByLoanId: {
          id: { equalTo: $loanId }
          accountByBorrowerId: { idSs58: { equalTo: $idSs58 } }
        }
      }
    ) {
      nodes {
        id
        type
        loanId
        timestamp
        amount
        amountValueUsd
        event: eventByEventId {
          block
          indexInBlock
        }
        loan: loanByLoanId {
          asset
          borrowerType
          borrower: accountByBorrowerId {
            idSs58
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`);

export const getAllLiquidationsQuery = gql(/* GraphQL */ `
  query GetAllLiquidations($first: Int, $offset: Int, $loanId: BigInt, $idSs58: String) {
    allLiquidationSwapRequests(
      first: $first
      offset: $offset
      orderBy: CREATED_AT_EVENT_ID_DESC
      filter: {
        loanByLoanId: {
          id: { equalTo: $loanId }
          accountByBorrowerId: { idSs58: { equalTo: $idSs58 } }
        }
      }
    ) {
      nodes {
        swapRequestId
        isHard
        createdAtEvent: eventByCreatedAtEventId {
          timestamp
        }
        loan: loanByLoanId {
          id
          borrower: accountByBorrowerId {
            idSs58
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`);

export const getLoanById = gql(/* GraphQL */ `
  query GetLoanById($loanId: BigInt!) {
    loan: loanById(id: $loanId) {
      ...LoanInfo
    }
  }
`);

gql(/* GraphQL */ `
  fragment LoanInfo on Loan {
    asset
    id
    isLiquidated
    createdAtTimestamp
    lastUpdatedAtTimestamp
    borrower: accountByBorrowerId {
      idSs58
    }
    borrowerType
    loanUpdates: loanUpdatesByLoanId(orderBy: TIMESTAMP_ASC) {
      nodes {
        type
        amount
        amountValueUsd
        timestamp
        event: eventByEventId {
          block
          indexInBlock
        }
      }
    }
    totalFees: loanFeesByLoanId {
      agg: aggregates {
        sum {
          totalAmount
          totalAmountValueUsd
        }
      }
    }
    fees: loanFeesByLoanId(filter: { type: { notIn: LIQUIDATION } }) {
      daily: groupedAggregates(groupBy: TIMESTAMP_TRUNCATED_TO_DAY) {
        keys
        sum {
          totalAmount
          totalAmountValueUsd
        }
        max {
          timestamp
        }
      }
    }
    liquidationFees: loanFeesByLoanId(filter: { type: { in: LIQUIDATION } }) {
      nodes {
        totalAmount
        totalAmountValueUsd
        timestamp
      }
    }

    inLiquidationSwaps: liquidationSwapRequestsByLoanId(
      filter: {
        createdAtEventId: { isNull: false }
        completedAtEventId: { isNull: true }
        abortedAtEventId: { isNull: true }
      }
    ) {
      nodes {
        createdAtEventId
        completedAtEventId
        abortedAtEventId
      }
    }
    allLiquidationSwaps: liquidationSwapRequestsByLoanId(
      filter: { createdAtEventId: { isNull: false } }
    ) {
      nodes {
        isHard
        swapRequestId
        createdAtEventId
        completedAtEventId
        abortedAtEventId
      }
    }
    createdAtEvent: eventByCreatedAtEventId {
      block
      indexInBlock
    }
    settledAtEvent: eventBySettledAtEventId {
      block
      indexInBlock
    }
    boost: boostByBoostId {
      swapRequestId
    }
  }
`);

export const getLendingBorrowApyStatsQuery = gql(/* GraphQL */ `
  query GetLendingBorrowApyStats(
    $asset: ChainflipAsset!
    $minTimestamp: Datetime!
    $maxTimestamp: Datetime!
  ) {
    lendingPoolStats: allLendingPoolStats(
      filter: {
        asset: { equalTo: $asset }
        and: [
          { timestamp: { greaterThanOrEqualTo: $minTimestamp } }
          { timestamp: { lessThanOrEqualTo: $maxTimestamp } }
        ]
      }
      orderBy: BLOCK_DESC
    ) {
      groupedAggregates(groupBy: [ASSET, TIMESTAMP_TRUNCATED_TO_DAY]) {
        keys
        average {
          avgInterestRateBps
          projectedBorrowApy6H
          projectedBorrowApy24H
          projectedBorrowApy72H
          projectedBorrowApy168H
          projectedBorrowApy720H
        }
      }
    }
  }
`);

export const getLendingSupplyApyStatsQuery = gql(/* GraphQL */ `
  query GetLendingSupplyApyStats(
    $asset: ChainflipAsset!
    $minTimestamp: Datetime!
    $maxTimestamp: Datetime!
  ) {
    lendingPoolStats: allLendingPoolStats(
      filter: {
        asset: { equalTo: $asset }
        and: [
          { timestamp: { greaterThanOrEqualTo: $minTimestamp } }
          { timestamp: { lessThanOrEqualTo: $maxTimestamp } }
        ]
      }
      orderBy: BLOCK_DESC
    ) {
      groupedAggregates(groupBy: [ASSET, TIMESTAMP_TRUNCATED_TO_DAY]) {
        keys
        average {
          projectedApy6H
          projectedApy24H
          projectedApy72H
          projectedApy168H
          projectedApy720H
        }
      }
    }
  }
`);

export const getAllIncentiveSeasonsQuery = gql(/* GraphQL */ `
  query GetAllIncentiveSeasons {
    allIncentiveSeasons(orderBy: SEASON_INDEX_DESC) {
      nodes {
        isActive
        seasonIndex
        startBlockHeight
        startBlockDate
        endBlockHeight
        endBlockDate
        isActive
        program: lendingPoolIncentiveProgramsByIncentiveSeasonId {
          nodes {
            id
            lendingPool: lendingPoolByLendingPoolId {
              asset
            }
            type
            intervalBlocks: pointDistributionIntervalBlocks
            pointsPerInterval: pointsDistributedPerInterval
            totalPoints: maxAllocatedPoints
            totalFlip: maxAllocatedFlipAmount
          }
        }
      }
    }
  }
`);

export const getLpFlipDistributionInfoQuery = gql(/* GraphQL */ `
  query GetLpFlipDistributionInfo($lpIdSs58: String, $seasonIndex: Int!) {
    flipDistributions: allLpFlipAirdropDistributionEvents(
      filter: {
        lendingPoolIncentiveProgramByLendingPoolIncentiveProgramId: {
          incentiveSeasonByIncentiveSeasonId: { seasonIndex: { equalTo: $seasonIndex } }
        }
        lpSs58Id: { equalTo: $lpIdSs58 }
      }
    ) {
      nodes {
        lpSs58Id
        txRef
        flipAmount
        pointAmount
        flipAmountUsd
        lendingPoolIncentiveProgramId
      }
    }
  }
`);

export const getLpSupplyPnl = gql(/* GraphQL */ `
  query GetLpSupplyPnl(
    $idSs58: String!
    $groupBy: [LpLendingPoolSnapshotGroupBy!]!
    $filters: LpLendingPoolSnapshotFilter
  ) {
    account: accountByIdSs58(idSs58: $idSs58) {
      snapshots: lpLendingPoolSnapshotsByLiquidityProviderId(
        filter: $filters
        orderBy: TIMESTAMP_DESC
      ) {
        agg: groupedAggregates(groupBy: $groupBy) {
          keys
          sum {
            supplyPnl1H
            supplyPnl1HUsd
          }
        }
      }
    }
  }
`);

export const getLpLendingPositionOpenings = gql(/* GraphQL */ `
  query GetLpLendingPositionOpenings($idSs58: String!, $assets: [ChainflipAsset!]) {
    account: accountByIdSs58(idSs58: $idSs58) {
      balanceChanges: lendingLiquidityBalanceChangesByLiquidityProviderId(
        filter: { isPositionOpening: { equalTo: true }, asset: { in: $assets } }
        orderBy: EVENT_ID_DESC
      ) {
        groupedAggregates(groupBy: ASSET) {
          keys
          max {
            timestamp
          }
        }
      }
    }
  }
`);

export const getLpTotalSupplyPnl = gql(/* GraphQL */ `
  query GetLpTotalSupplyPnl($idSs58: String!, $minTimestamp: Datetime, $maxTimestamp: Datetime) {
    account: accountByIdSs58(idSs58: $idSs58) {
      snapshots: lpLendingPoolSnapshotsByLiquidityProviderId(
        filter: {
          and: [
            { timestamp: { greaterThanOrEqualTo: $minTimestamp } }
            { timestamp: { lessThanOrEqualTo: $maxTimestamp } }
          ]
        }
      ) {
        agg: aggregates {
          sum {
            supplyPnl1HUsd
          }
        }
      }
    }
  }
`);
